import { useId, useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { Badge, Btn, Modal, Select, TextArea } from '../components/ui';
import { dateTime, unitCategoryOf, unitName, UNIT_STATUS } from '../format';
import type { CompanyInfo, Place, TripStop, Unit } from '../types';

// Un <label> solo puede nombrar UN control; el encabezado de un grupo va en
// un <span> con el aire de `.field > label` y el contenedor lo anuncia con
// role="group" + aria-labelledby.
const FIELD_CAPTION: CSSProperties = { fontSize: 13.5, fontWeight: 600 };

export interface TripFormValue { driverId: string; stops: TripStop[]; notes: string; tractorUnitId: string | null; trailerUnitId: string | null }

// Topes del backend (service.mjs): 1–20 paradas, cantidades 0–999, nota de
// parada 500 y notas del viaje 1000. El formulario debe cortar antes para que
// el servidor no rechace el envío.
const STOPS_MAX = 20;
const QTY_MAX = 999;

/** Lista de paradas de un viaje ya resuelta a nombres de lugar, con carga y
    nota por parada. La comparten la vista del chofer y la de la oficina.
    `badge` es opcional: si lo da la vista, marca paradas por placeId (p. ej.
    «Lugar de mi empresa» para los frecuentes de la empresa). */
export function TripStops({ stops, placeName, badge }: { stops: TripStop[]; placeName(id: string): string; badge?(placeId: string): ReactNode }) {
  return <ol className="small trip-stops">
    {stops.map((s, i) => {
      const mark = badge?.(s.placeId);
      return (
        <li key={`${s.placeId}-${i}`}>
          {placeName(s.placeId)}
          {mark ? <> {mark}</> : null}
          {(s.pallets || s.partialPallets || s.looseBoxes) ? ` · ${[s.pallets ? `${s.pallets} tarimas` : '', s.partialPallets ? `${s.partialPallets} parciales` : '', s.looseBoxes ? `${s.looseBoxes} cajas` : ''].filter(Boolean).join(', ')}` : ''}
          {s.note ? ` — ${s.note}` : ''}
        </li>
      );
    })}
  </ol>;
}

/** Progreso por parada que el servidor infiere de las visitas del chofer
    (`stop.progress`, solo llega en viajes abiertos): fábrica de la función
    `badge` de TripStops — 'visiting' → «En visita», 'done' → «Visitada» con su
    hora y las piezas entregadas; sin progress → null (la pendiente no lleva
    marca). La comparten la oficina y el chofer. */
export function tripProgressBadge(stops: TripStop[]): (placeId: string) => ReactNode {
  const byPlace = new Map(stops.map(s => [s.placeId, s.progress ?? null]));
  return (placeId: string): ReactNode => {
    const pr = byPlace.get(placeId);
    if (!pr) return null;
    return pr.status === 'visiting'
      ? <Badge tone="info">En visita</Badge>
      : <Badge tone="ok">Visitada {dateTime(pr.at)}{pr.delivered > 0 ? ` · ${pr.delivered} piezas` : ''}</Badge>;
  };
}

/** Formulario de viaje compartido entre «Asignar viaje» y «Editar viaje»:
    chofer (solo al asignar), paradas multi-selección con carga por parada y notas. */
export function TripForm({ title, places, favoriteIds, drivers, fixedDriverName, units, company, initial, busy, submitLabel, onSubmit, onClose }: {
  title: string;
  places: Place[];
  /** Empresa del contexto: resuelve tipos personalizados en los selectores. */
  company?: CompanyInfo | null;
  /** Lugares frecuentes de la empresa (snapshot.companyFavoriteIds): se listan
      primero en «Agregar lugar…». Sin empresa llega vacío y el select queda plano. */
  favoriteIds?: string[];
  drivers: { uid: string; displayName: string; defaultTractorId?: string | null; defaultTrailerId?: string | null }[];
  /** Flota de la empresa (snapshot.companyUnits): habilita los selectores
      opcionales de tracto y caja. Ausente = formulario sin unidades. */
  units?: Unit[];
  /** Cuando se edita un viaje el chofer ya está fijado — se muestra, no se elige. */
  fixedDriverName?: string;
  initial: TripFormValue;
  busy: boolean;
  submitLabel: string;
  onSubmit: (value: TripFormValue) => Promise<void> | void;
  onClose: () => void;
}) {
  const [driverId, setDriverId] = useState(initial.driverId);
  const [stops, setStops] = useState<TripStop[]>(initial.stops);
  const [notes, setNotes] = useState(initial.notes);
  const [tractorUnitId, setTractorUnitId] = useState(initial.tractorUnitId ?? '');
  const [trailerUnitId, setTrailerUnitId] = useState(initial.trailerUnitId ?? '');
  const [pick, setPick] = useState('');
  // ids de los encabezados de grupo / valor fijo (label htmlFor y
  // aria-labelledby) — useId evita colisiones entre instancias.
  const driverOutId = useId();
  const unitsCapId = useId();
  const stopsCapId = useId();

  const byId = useMemo(() => new Map(places.map(p => [p.id, p])), [places]);
  const available = places.filter(p => !stops.some(s => s.placeId === p.id));
  // Frecuentes de la empresa primero: se reparte `available` (ya alfabético) en
  // dos grupos sin duplicar placeId; si ninguno queda disponible el select
  // queda plano como siempre (formulario sin empresa incluido).
  const fav = useMemo(() => new Set(favoriteIds ?? []), [favoriteIds]);
  const availableFav = available.filter(p => fav.has(p.id));
  const availableRest = available.filter(p => !fav.has(p.id));
  const grouped = availableFav.length > 0;
  const optionLabel = (p: Place) => `${p.name} — ${p.zone}${p.companyId ? ' (privado)' : ''}`;
  const patch = (i: number, part: Partial<TripStop>) => setStops(list => list.map((s, j) => j === i ? { ...s, ...part } : s));
  const qty = (raw: string) => {
    const n = Math.trunc(Number(raw));
    // Carga por parada 0–999: lo que pase del tope se recorta, no se anula.
    return Number.isFinite(n) ? Math.min(QTY_MAX, Math.max(0, n)) : 0;
  };
  const addStop = () => {
    if (!pick || !byId.has(pick) || stops.length >= STOPS_MAX) return;
    setStops(list => [...list, { placeId: pick, pallets: 0, partialPallets: 0, looseBoxes: 0, note: '' }]);
    setPick('');
  };

  // Unidades elegibles para el viaje: el vehículo (motriz) y el remolque
  // (remolcado) no vendidos. Taller/fuera de servicio quedan a la vista pero
  // bloqueadas — el backend las rechaza y la UI no las deja elegir.
  const tractors = useMemo(() => (units ?? []).filter(u => unitCategoryOf(u, company) === 'vehicle' && u.status !== 'sold'), [units, company]);
  const trailers = useMemo(() => (units ?? []).filter(u => unitCategoryOf(u, company) === 'attachment' && u.status !== 'sold'), [units, company]);
  // Si un remolque lleva equipo adosado (reefer suelto vía parentUnitId) o
  // el reefer va embebido como componente (caja refrigerada), viaja con él.
  const reeferOf = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of units ?? []) {
      if (u.parentUnitId) map.set(u.parentUnitId, unitName(u, company));
      const reefer = (u.components ?? []).find(c => c.kind === 'reefer');
      if (reefer && !map.has(u.id)) map.set(u.id, `el reefer ${[reefer.brand, reefer.model].filter(Boolean).join(' ') || ''}`.trim());
    }
    return map;
  }, [units, company]);
  const activeTractor = useMemo(() => new Set(tractors.filter(u => u.status === 'active').map(u => u.id)), [tractors]);
  const activeTrailer = useMemo(() => new Set(trailers.filter(u => u.status === 'active').map(u => u.id)), [trailers]);

  // Al elegir chofer, sus unidades habituales (defaultTractorId/defaultTrailerId)
  // autollenan el viaje — pero solo si la unidad sigue activa.
  const chooseDriver = (uid: string) => {
    setDriverId(uid);
    const d = drivers.find(x => x.uid === uid);
    if (!d) { setTractorUnitId(''); setTrailerUnitId(''); return; }
    setTractorUnitId(d.defaultTractorId && activeTractor.has(d.defaultTractorId) ? d.defaultTractorId : '');
    setTrailerUnitId(d.defaultTrailerId && activeTrailer.has(d.defaultTrailerId) ? d.defaultTrailerId : '');
  };

  const unitOption = (u: Unit) =>
    <option key={u.id} value={u.id} disabled={u.status !== 'active'}>
      {unitName(u, company)}{u.status !== 'active' ? ` (${(UNIT_STATUS[u.status]?.label ?? u.status).toLowerCase()})` : ''}
    </option>;

  const valid = driverId !== '' && stops.length > 0;

  return <Modal title={title} onClose={onClose} wide>
    {fixedDriverName !== undefined
      /* Chofer fijo al editar: valor de solo lectura — <output> sí es
         etiquetable, así el <label> conserva a qué apunta. */
      ? <div className="field"><label htmlFor={driverOutId}>Chofer</label><output id={driverOutId} className="name">{fixedDriverName}</output></div>
      : <Select label="Chofer" value={driverId} onChange={e => chooseDriver(e.target.value)}>
          <option value="">Elige al chofer…</option>
          {drivers.map(d => <option key={d.uid} value={d.uid}>{d.displayName || 'Sin nombre'}</option>)}
        </Select>}

    {units !== undefined && <div className="field" role="group" aria-labelledby={unitsCapId}>
      <span id={unitsCapId} style={FIELD_CAPTION}>Unidades (opcional)</span>
      <div className="form-grid">
        <select className="select" value={tractorUnitId} onChange={e => setTractorUnitId(e.target.value)} aria-label="Vehículo del viaje">
          <option value="">Sin vehículo…</option>
          {tractors.map(unitOption)}
        </select>
        <select className="select" value={trailerUnitId} onChange={e => setTrailerUnitId(e.target.value)} aria-label="Remolque del viaje">
          <option value="">Sin remolque…</option>
          {trailers.map(unitOption)}
        </select>
      </div>
      {trailerUnitId && reeferOf.get(trailerUnitId)
        ? <span className="hint">El remolque lleva {reeferOf.get(trailerUnitId)} — va incluido en el viaje.</span>
        : <span className="hint">Se autollenan con la unidad habitual del chofer; las que están en taller no se pueden elegir.</span>}
    </div>}

    <div className="field" role="group" aria-labelledby={stopsCapId}>
      <span id={stopsCapId} style={FIELD_CAPTION}>Paradas del viaje</span>
      {stops.length === 0 && <span className="hint">Agrega al menos una parada: lugar, piezas y una nota opcional para el chofer.</span>}
      {stops.length > 0 && <div>
        <div className="stop-row head"><span className="head">Lugar</span><span className="head">Tarimas</span><span className="head">Parciales</span><span className="head">Cajas</span><span className="head">Nota</span><span /></div>
        {stops.map((stop, i) => {
          const place = byId.get(stop.placeId);
          return <div className="stop-row" key={stop.placeId}>
            <div><div className="name">{place?.name ?? 'Lugar no disponible'}</div><div className="tiny muted">{place ? `${place.zone}, ${place.country}` : stop.placeId}</div></div>
            <input className="input num" type="number" min={0} max={QTY_MAX} inputMode="numeric" value={stop.pallets} aria-label="Tarimas" onChange={e => patch(i, { pallets: qty(e.target.value) })} />
            <input className="input num" type="number" min={0} max={QTY_MAX} inputMode="numeric" value={stop.partialPallets} aria-label="Tarimas parciales" onChange={e => patch(i, { partialPallets: qty(e.target.value) })} />
            <input className="input num" type="number" min={0} max={QTY_MAX} inputMode="numeric" value={stop.looseBoxes} aria-label="Cajas sueltas" onChange={e => patch(i, { looseBoxes: qty(e.target.value) })} />
            <input className="input" value={stop.note} maxLength={500} placeholder="Nota de la parada" aria-label="Nota de la parada" onChange={e => patch(i, { note: e.target.value })} />
            <Btn tone="ghost" small onClick={() => setStops(list => list.filter((_, j) => j !== i))}>Quitar</Btn>
          </div>;
        })}
      </div>}
      <div className="row u-mt-sm">
        <select className="select grow" value={pick} onChange={e => setPick(e.target.value)} aria-label="Agregar lugar" disabled={stops.length >= STOPS_MAX}>
          <option value="">Agregar lugar…</option>
          {grouped ? <>
            <optgroup label="Frecuentes de la empresa">
              {availableFav.map(p => <option key={`fav:${p.id}`} value={p.id}>{optionLabel(p)}</option>)}
            </optgroup>
            {availableRest.length > 0 && <optgroup label="Todos los lugares">
              {availableRest.map(p => <option key={`all:${p.id}`} value={p.id}>{optionLabel(p)}</option>)}
            </optgroup>}
          </> : available.map(p => <option key={p.id} value={p.id}>{optionLabel(p)}</option>)}
        </select>
        <Btn tone="secondary" small onClick={addStop} disabled={!pick || stops.length >= STOPS_MAX}>Agregar</Btn>
      </div>
      {stops.length >= STOPS_MAX && <span className="hint">Máximo {STOPS_MAX} paradas por viaje.</span>}
    </div>

    <TextArea label="Notas del viaje" value={notes} onChange={e => setNotes(e.target.value)} maxLength={1000} hint="Indicaciones generales para el chofer (orden sugerido, horarios, referencias)." />

    <div className="actions end modal-actions">
      <Btn tone="ghost" onClick={onClose}>Cancelar</Btn>
      <Btn busy={busy} disabled={!valid} onClick={() => void onSubmit({ driverId, stops, notes, tractorUnitId: tractorUnitId || null, trailerUnitId: trailerUnitId || null })}>{submitLabel}</Btn>
    </div>
  </Modal>;
}
